import { useEffect, useRef, useState } from 'react';
import { createFromSpec, describeTree, nodeToSpec, solid, parseColor, type NodeData, type NodeId, type NodeSpec } from '@vellum/core';
import { app } from '../app.ts';
import { useApp } from '../hooks.ts';
import { timerOf, timerAction } from '../timer.ts';
import { Icons } from './icons.tsx';

// ---------------------------------------------------------------- cursor chat

/** "/" opens a chat bubble at your cursor; peers see it next to your cursor. */
export function CursorChat() {
  const open = useApp('ui', () => app.ui.chatOpen);
  const [text, setText] = useState('');
  const pos = useApp('camera', () => app.lastPointer);
  useEffect(() => {
    if (!open) setText('');
  }, [open]);
  if (!open || !pos) return null;
  const s = app.pageToScreen(pos);
  return (
    <div className="cursor-chat" style={{ left: s.x + 14, top: s.y + 14 }}>
      <input
        autoFocus
        value={text}
        maxLength={140}
        placeholder="Say something"
        onChange={(e) => {
          setText(e.target.value);
          app.sync?.presence({ status: e.target.value ? `💬 ${e.target.value}` : undefined });
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Escape' || (e.key === 'Enter' && !text)) {
            app.sync?.presence({ status: undefined });
            app.setUI({ chatOpen: false });
          }
          if (e.key === 'Enter' && text) {
            // keep the bubble visible a moment, then clear
            setTimeout(() => app.sync?.presence({ status: undefined }), 5000);
            app.setUI({ chatOpen: false });
          }
        }}
        onBlur={() => app.setUI({ chatOpen: false })}
      />
    </div>
  );
}

// ---------------------------------------------------------------- timer

export function Timer() {
  useApp('doc', () => app.docVersion);
  const page = app.doc.get(app.editor.pageId);
  const t = timerOf(page);
  const [, tick] = useState(0);
  useEffect(() => {
    if (t.state !== 'RUNNING') return;
    const iv = setInterval(() => tick((x) => x + 1), 250);
    return () => clearInterval(iv);
  }, [t.state]);
  if (!page || page.pageKind !== 'WHITEBOARD') return null;
  const left = Math.ceil(t.remaining) * 1000;
  const mm = Math.floor(left / 60000),
    ss = Math.floor((left % 60000) / 1000);
  const done = t.state === 'RUNNING' && t.remaining === 0;
  return (
    <div className={`timer ${done ? 'done' : ''}`} onPointerDown={(e) => e.stopPropagation()}>
      {t.state !== 'STOPPED' ? (
        <>
          <b>{done ? "Time's up" : `${mm}:${String(ss).padStart(2, '0')}`}</b>
          {!done && (
            <button className="icon-btn" title={t.state === 'PAUSED' ? 'Resume' : 'Pause'} onClick={() => timerAction(t.state === 'PAUSED' ? 'resume' : 'pause')}>
              {t.state === 'PAUSED' ? '▶' : '❚❚'}
            </button>
          )}
          <button className="btn ghost" onClick={() => timerAction('add', 60)}>
            +1m
          </button>
          <button className="icon-btn" onClick={() => timerAction('stop')}>
            {Icons.close}
          </button>
        </>
      ) : (
        <>
          <span>⏲</span>
          {[1, 3, 5, 10].map((m) => (
            <button key={m} className="btn ghost" onClick={() => timerAction('start', m * 60)}>
              {m}m
            </button>
          ))}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- script console

const EXAMPLE = `// The same API agents get via run_script. Runs as one undoable step.
const frames = findAll(n => n.type === 'FRAME' && n.parent === pageId);
frames.forEach((f, i) => moveTo(f.id, i * 1300, 0));
return frames.length + ' frames arranged';`;

export function ScriptConsole() {
  const open = useApp('ui', () => app.ui.scriptOpen);
  const [code, setCode] = useState(() => {
    try {
      return localStorage.getItem('vellum-script') ?? EXAMPLE;
    } catch {
      return EXAMPLE;
    }
  });
  const [out, setOut] = useState('');
  const ta = useRef<HTMLTextAreaElement>(null);
  if (!open) return null;
  const run = () => {
    try {
      localStorage.setItem('vellum-script', code);
    } catch {
      /* ignore */
    }
    const logs: string[] = [];
    const ed = app.editor;
    const doc = app.doc;
    const pageId = ed.pageId;
    const api: Record<string, unknown> = {
      doc,
      editor: ed,
      pageId,
      get: (id: NodeId) => doc.get(id),
      children: (id: NodeId = pageId) => doc.children(id),
      findAll: (pred: (n: NodeData) => boolean = () => true, within: NodeId = pageId) => doc.subtree(within).filter((n) => n.id !== within && pred(n)),
      findOne: (pred: (n: NodeData) => boolean, within: NodeId = pageId) => doc.subtree(within).find((n) => n.id !== within && pred(n)),
      create: (specs: NodeSpec | NodeSpec[], parentId: NodeId = pageId) => createFromSpec(ed, specs, parentId),
      set: (id: NodeId, props: Partial<NodeData>) => ed.update(id, props),
      moveTo: (id: NodeId, x: number, y: number) => ed.setPagePosition(id, x, y),
      move: (ids: NodeId | NodeId[], dx: number, dy: number) => ed.moveBy(Array.isArray(ids) ? ids : [ids], dx, dy),
      remove: (ids: NodeId | NodeId[]) => ed.remove(Array.isArray(ids) ? ids : [ids]),
      select: (ids: NodeId[]) => ed.setSelection(ids),
      selection: () => ed.selectedNodes(),
      spec: (id: NodeId, depth = 2) => nodeToSpec(doc, id, depth),
      outline: (id: NodeId = pageId) => describeTree(doc, id, 4),
      solid,
      color: parseColor,
      log: (...a: unknown[]) => logs.push(a.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))).join(' ')),
    };
    try {
      let result: unknown;
      ed.transact('Script', () => {
        // eslint-disable-next-line no-new-func
        result = new Function(...Object.keys(api), `"use strict";\n${code}`)(...Object.values(api));
      });
      setOut([...logs, result !== undefined ? `→ ${typeof result === 'string' ? result : JSON.stringify(result, null, 1)}` : '✓ done'].join('\n'));
    } catch (e) {
      setOut([...logs, `✗ ${(e as Error).message} (rolled back)`].join('\n'));
    }
  };
  return (
    <div className="modal-backdrop" onPointerDown={(e) => e.target === e.currentTarget && app.setUI({ scriptOpen: false })}>
      <div className="modal" style={{ width: 'min(760px, calc(100vw - 32px))' }}>
        <header>
          <span>Script console</span>
          <span style={{ display: 'flex', gap: 6 }}>
            <button className="btn primary" onClick={run}>
              Run ⌘↵
            </button>
            <button className="icon-btn" onClick={() => app.setUI({ scriptOpen: false })}>
              {Icons.close}
            </button>
          </span>
        </header>
        <div className="body">
          <textarea
            ref={ta}
            className="script-input"
            spellCheck={false}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) run();
              if (e.key === 'Tab') {
                e.preventDefault();
                const t = e.currentTarget;
                const p = t.selectionStart;
                setCode(code.slice(0, p) + '  ' + code.slice(t.selectionEnd));
                requestAnimationFrame(() => t.setSelectionRange(p + 2, p + 2));
              }
            }}
          />
          {out && <pre style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>{out}</pre>}
        </div>
      </div>
    </div>
  );
}
